import React, { useEffect } from "react"
import { AuthenticationContextProvider } from "./authentication-context"
import { signInToDoorhubWithGoogle } from "./sign-in-to-doorhub-with-google"
import { firebase } from "../firebase-web"
import SignInScreen from "../../screens/SignIn/SignInScreen"
import { setApolloAuthToken } from "../ApolloClient"
import { Image, TouchableWithoutFeedback } from "react-native"
import { Authentication } from "./authentication-types"
import { gotoSamlAuth, signInToDoorhubWithSaml, useSamlReturnDetector } from "./sign-in-to-doorhub-with-saml"
import { gotoPortalAuth, signInToDoorhubWithPortal, usePortalOauthReturnDetector } from "./sign-in-to-doorhub-with-portal"
import { useAsync } from "pusatec-react-utility"
import Loading from "../../components/Loading"
import AsyncStorage from "@react-native-async-storage/async-storage"
import { parseDoorhubToken } from "./parse-doorhub-token"
import { ErrorMessage } from "pusatec-react-native"
import { BuildConfiguration } from "../platform-info/build-configuration"
import { TouchButton } from "pusatec-react-native"
import { useTranslation } from "react-i18next"

const auth = firebase.auth()
const authProvider = new firebase.auth.GoogleAuthProvider()

interface Props {
	signedInContent: any
}

const DOORHUB_TOKEN_ID = "doorhub_token"

export function AuthenticationProvider(props: Props): JSX.Element | null {
	const { signedInContent } = props

	const [call, state] = useAsync<Authentication | null>()
	const authentication = state.data ?? null
	const [portalError, setPortalError] = React.useState<string | undefined>(undefined)
	const [apolloAuthReady, setApolloAuthReady] = React.useState(false)
	const { t } = useTranslation()

	// USE DEV DOORHUB TOKEN TO BYPASS SIGNIN WHILE IN DEV
	useEffect(() => {
		if (BuildConfiguration.DEV_DOORHUB_TOKEN) {
			call(async () => {
				return parseDoorhubToken(BuildConfiguration.DEV_DOORHUB_TOKEN!)
			})
		}
	}, [])

	useEffect(() => {
		setApolloAuthToken(state.data?.token ?? null)

		if (state.data?.token) {
			AsyncStorage.setItem(DOORHUB_TOKEN_ID, state.data?.token ?? null)
			setApolloAuthReady(true)
		} else {
			setApolloAuthReady(false)
		}
	}, [state.data?.token])

	useEffect(() => {
		AsyncStorage.getItem(DOORHUB_TOKEN_ID).then(token => {
			if (token) {
				const authentication = parseDoorhubToken(token)
				console.info({ authentication, now: new Date().getTime() })
				if (new Date().getTime() > authentication.exp * 1000) {
					console.info("Found token in localStorage but it was expired")
				} else {
					call(async () => authentication)
				}
			}
		})
	}, [])

	// Firebase might sign the user in automatically and trigger this. ??
	useEffect(() => {
		if (!authentication) {
			const unsubscribe = auth.onAuthStateChanged(async user => {
				if (user && !state.called) {
					call(async () => {
						console.info("Auth state changed", { user })
						const idToken = await user?.getIdToken()
						return await signInToDoorhubWithGoogle(idToken)
					})
				}
			})

			return unsubscribe
		}
	}, [authentication != null])

	useSamlReturnDetector((samlLoginAuthCode) => {
		call(async () => {
			return await signInToDoorhubWithSaml(samlLoginAuthCode)
		})
	})

	usePortalOauthReturnDetector((portalCode) => {
		call(async () => {
			return await signInToDoorhubWithPortal(portalCode)
		})
	}, (message) => setPortalError(message))

	if (authentication && !apolloAuthReady) {
		return <Loading />
	}

	if (!authentication) {
		return <SignInScreen signinError={undefined}>
			{state.loading ? <Loading /> : <>
				<TouchableWithoutFeedback
					style={{ margin: 10 }}
					onPress={() => {
						call(async () => {
							const result = await auth.signInWithPopup(authProvider)
							const user = result.user

							if (!user) throw Error(t("Google signin user not found"))

							console.info(`Signed in with google ${user}`)
							const idToken = await user.getIdToken()
							return await signInToDoorhubWithGoogle(idToken)
						})
					}}
				>
					<Image
						source={require("../../../assets/google-signin-button/btn_google_signin_light_normal_web@2x.png")}
						resizeMode="contain"
						style={{ width: 260, height: 80 }}
					/>
				</TouchableWithoutFeedback>

				<TouchButton
					type="primary"
					text={t("Login with portal")}
					onPress={async () => {
						try {
							await gotoPortalAuth()
						} catch (e: any) {
							setPortalError(e?.message || t("Failed to start portal login"))
						}
					}}
					containerStyle={{ marginTop: 10, alignSelf: "center" }}
				/>

				<TouchableWithoutFeedback
					style={{ margin: 10, marginTop: 30 }}
					onPress={() => gotoSamlAuth()}
				>
					<Image
						source={require("../../../assets/Haka_login_vaaka.jpg")}
						resizeMode="contain"
						style={{ width: 260, height: 80 }}
					/>
				</TouchableWithoutFeedback>
			</>}

			{state.error ? <ErrorMessage extra={state.error} /> : null}
			{portalError ? <ErrorMessage message={portalError} /> : null}
		</SignInScreen>
	} else {
		console.info({ authentication })
		return (
			<AuthenticationContextProvider
				authentication={authentication}
				signOut={async () => {
					await auth.signOut()
					await AsyncStorage.removeItem(DOORHUB_TOKEN_ID)
					setApolloAuthToken(null)
					call(async () => null)
				}}>
				{signedInContent}
			</AuthenticationContextProvider>
		)
	}
}
